import axios from "axios";
import React, { useState, useEffect } from "react";
import {
  Button,
  Alert,
  Card,
  Flex,
  Box,
  Switch,
  Title,
  Tabs,
  Grid,
} from "@mantine/core";
import { useParams, Link, Route, Routes } from "react-router-dom";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IconExternalLink } from "@tabler/icons-react";

import { EmailServerCreate } from "@components/EmailServer";
import {
  BulkCommunicationManagement,
  BulkCommunicationAllowToggle,
  EntityMessagingSms,
} from "@components/BulkCommunication";
import {
  LocationContesting,
  LocationForm,
  LocationReadOnly,
  LocationSettings,
} from "@components/Location";
import { AssetManagement, LogoUpload } from "@components/Asset";
import { CampaignManagement } from "@components/Campaign";
import { EffortManagement } from "@components/Effort";
import {
  EntitySettingsCode,
  ErrorView,
  PageTitle,
  QrCodeGenerator,
  RequestApiConnectionButton,
  Subnav,
} from "@components/shared";
import { GeospotManagement, GeospotItem } from "@components/Geospot";
import { KeywordManagement } from "@components/Keyword";
import { LootLockerManagement } from "@components/LootLocker";
import { ManagerManagement } from "@components/Manager";
import { StationPlays, StationFeedLoaderButton } from "@components/Station";
import { SongQueryManagement } from "@components/SongQuery";
import { MessageList } from "@components/Message";
import { EmailAppConfig } from "@components/AppConfig";
import { UserGroupManagement } from "@components/UserGroup";
import { AffiliateJoinManagement } from "@components/Affiliate";

import { locationVariety } from "@components/Location/helpers";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function LocationDetail({ isProfile = false, isAdmin = false }) {
  const queryClient = useQueryClient();
  const params = useParams();
  const managerInfo = useSelector((state) => state.manager);
  const { id } = params;
  const queryKey = `locations${id}`;

  const { failureReason, data: info } = useQuery({
    queryKey: [queryKey],
    queryFn: async () =>
      axios
        .get(`/locations/${params.id}/`)
        .then(({ data }) => {
          return data.response[0];
        })
        .catch((err) => {
          throw err;
        }),
  });

  const fetchData = () => queryClient.invalidateQueries([queryKey]);

  if (failureReason) {
    return <ErrorView text={failureReason} />;
  }

  if (!info) return null;

  const showTextOnly = managerInfo && info.text_only;

  const postmarkServerId =
    info.postmark_servers && JSON.stringify(info.postmark_servers) !== "{}"
      ? Object.keys(info.postmark_servers)[0]
      : null;

  const showSubnav =
    (info.settings_code && info.variety !== locationVariety.dealer) ||
    (postmarkServerId && !isProfile);

  const showSms =
    (info.allow_sms_marketing ||
      info.allow_transactional_sms ||
      info.allow_conversational_sms) &&
    !managerInfo;

  const links = [
    { to: `/locations/${id}`, text: "Detail" },
    {
      title: "Interaction",
      links: [
        {
          to: `/locations/${id}/campaigns`,
          text: "Campaigns",
          visible: !isProfile,
        },
        {
          to: `/locations/${id}/efforts`,
          text: "Efforts",
          visible: !isProfile,
        },
        {
          to: `/locations/${id}/contesting`,
          text: "Contesting",
          visible: !isProfile,
        },
        {
          to: `/locations/${id}/keywords`,
          text: "Keywords",
          visible: !isProfile,
        },
        {
          to: `/locations/${id}/messages`,
          text: "Messages",
          visible: !isProfile,
        },
        {
          to: `/locations/${id}/bulk-communication`,
          text: "Bulk Communication",
          visible: managerInfo ? false : true,
          // visible:
          //   postmarkServerId &&
          //   info.allow_email_marketing &&
          //   !managerInfo?.location_id
          //     ? true
          //     : false,
        },
        {
          to: `/locations/${id}/sms-messaging`,
          text: "SMS Messaging",
          visible: showSms,
        },
      ].filter((f) => f.visible !== false),
    },
    {
      title: "Station Features",
      links: [
        { to: `/locations/${id}/play`, text: "Play" },
        { to: `/locations/${id}/song-categories`, text: "Song Categories" },
      ],
      visible: info.variety === locationVariety.station,
    },
    {
      to: `/locations/${id}/managers`,
      text: "Managers",
      visible: !isProfile,
    },
    {
      to: `/locations/${id}/asset-library`,
      text: "Asset Library",
      visible: !isProfile,
    },
    { to: `/locations/${id}/geospots`, text: "Geo-Spots" },
    { to: `/locations/${id}/settings`, text: "Settings", visible: !isProfile },
    {
      to: `/locations/${id}/loot-locker`,
      text: "Loot Locker",
      visible: !isProfile,
    },
    {
      to: `/locations/${id}/segments`,
      text: "Segments",
      visible: !isProfile,
    },
    {
      to: `/locations/${id}/affiliates`,
      text: "Affiliates",
      visible: !isProfile,
    },
    { to: `/locations/${id}/look-and-feel`, text: "Look & Feel" },
  ]
    .filter((f) => f.visible !== false)
    .filter((f) => (f.links ? f.links.length > 0 : true));

  const locationFormProps = {
    info,
    id,
    fetchData,
  };

  if (!info.edit) {
    return (
      <React.Fragment>
        <PageTitle
          title={info.name}
          subtitle={<React.Fragment>Location</React.Fragment>}
          icon={entityIcon.location()}
          iconColor={entityColor.location}
          style={{ marginBottom: "2em" }}
        />
        <LocationReadOnly info={info} />
      </React.Fragment>
    );
  }

  if (
    info.variety === 1 &&
    (!info.call_letters || !info.frequency || !info.genre)
  ) {
    return (
      <React.Fragment>
        <Flex mb={info.settings_code ? 0 : "lg"}>
          <LogoUpload
            locationId={id}
            onSuccess={fetchData}
            url={info.logo_url}
          />
          <PageTitle
            title={info.name}
            subtitle={
              <React.Fragment>
                Location
                {!managerInfo && info.auth_authority
                  ? ` (${info.auth_authority.variety_formatted})`
                  : ""}
              </React.Fragment>
            }
            icon={entityIcon.location()}
            iconColor={entityColor.location}
            style={{ marginBottom: "0" }}
          />
        </Flex>
        <Alert title="More info needed!" color="yellow" mb="sm">
          Add call letters, frequency, and genre to this location to access
          additional features
        </Alert>
        <Card>
          <FormattedLocationForm {...locationFormProps} />
        </Card>
      </React.Fragment>
    );
  }

  return (
    <div>
      {info.third_party_ident && managerInfo && (
        <Box mb="lg">
          <RequestApiConnectionButton locationId={id} />
        </Box>
      )}
      <Flex mb={info.settings_code ? 0 : "lg"}>
        <LogoUpload locationId={id} onSuccess={fetchData} url={info.logo_url} />
        <PageTitle
          title={info.name}
          subtitle={
            <React.Fragment>
              Location
              {!managerInfo && info.auth_authority
                ? ` (${info.auth_authority.variety_formatted})`
                : ""}
            </React.Fragment>
          }
          icon={entityIcon.location()}
          iconColor={entityColor.location}
          style={{ marginBottom: "0", marginLeft: "0.8em" }}
        />
      </Flex>
      {!showTextOnly && (
        <div>
          <Flex mt="sm">
            <Subnav buttonColor={entityColor.location} links={links} />
          </Flex>
          {showSubnav && (
            <Flex gap="sm" mb="lg">
              {info.settings_code &&
                info.variety !== locationVariety.dealer && (
                  <Button
                    component="a"
                    variant="light"
                    radius="xl"
                    rightSection={<IconExternalLink size={16} />}
                    color="gray"
                    size="xs"
                    target="_blank"
                    href={`/user-login/${info.settings_code.code}`}
                  >
                    User Portal
                  </Button>
                )}
              {postmarkServerId && !isProfile && (
                <Button
                  component={Link}
                  variant="light"
                  radius="xl"
                  rightSection={<IconExternalLink size={16} />}
                  color="gray"
                  size="xs"
                  target="_blank"
                  to={`/postmark/${postmarkServerId}`}
                >
                  Email
                </Button>
              )}
            </Flex>
          )}
        </div>
      )}
      {showTextOnly ? (
        <Card mt="sm">
          <FormattedLocationForm {...locationFormProps} />
        </Card>
      ) : (
        <Routes>
          <Route
            exact
            path="/"
            element={
              <React.Fragment>
                <Grid>
                  <Grid.Col span={{ base: 12, md: managerInfo ? 6 : 12 }}>
                    <Card>
                      <FormattedLocationForm {...locationFormProps} />
                    </Card>
                  </Grid.Col>
                  {managerInfo && (
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <>
                        <GeospotItem
                          data={{
                            latitude: info.latitude,
                            longitude: info.longitude,
                            address_1: info.address_1,
                            city: info.city,
                            state: info.state_province,
                            zip: info.zip,
                            country: info.country,
                            name: info.name,
                          }}
                        />
                      </>
                    </Grid.Col>
                  )}
                </Grid>
                {isAdmin && info.variety === locationVariety.station && (
                  <Card mt="xs">
                    <FeedLoaderPolling
                      locationId={id}
                      fetchData={fetchData}
                      enabled={info.feed_loader_polling === true}
                    />
                  </Card>
                )}
                {!postmarkServerId && isAdmin && (
                  <EmailServerCreate fetchData={fetchData} locationId={id} />
                )}
                {isAdmin && (
                  <div>
                    <Card mt="xs">
                      <BulkCommunicationAllowToggle
                        fetchData={fetchData}
                        locationId={id}
                        value={info.allow_email_marketing}
                        disabled={postmarkServerId ? false : true}
                      />
                    </Card>
                    <Card mt="xs">
                      <BulkCommunicationAllowToggle
                        fetchData={fetchData}
                        locationId={id}
                        value={info.allow_sms_marketing}
                        smsMarketing
                      />
                    </Card>
                    <Card mt="xs">
                      <BulkCommunicationAllowToggle
                        fetchData={fetchData}
                        locationId={id}
                        value={info.allow_transactional_sms}
                        transactionalSms
                      />
                    </Card>
                    <Card mt="xs">
                      <BulkCommunicationAllowToggle
                        fetchData={fetchData}
                        locationId={id}
                        value={info.allow_conversational_sms}
                        conversationalSms
                      />
                    </Card>
                  </div>
                )}
                {info.microsite_gallery_code && (
                  <>
                    <Title mt="xl" mb="xs" order={4}>
                      Gallery QR Code
                    </Title>
                    <Card>
                      <QrCodeGenerator
                        text={`${import.meta.env.VITE_DOMAIN}/gallery/${
                          info.microsite_gallery_code
                        }`}
                      />
                    </Card>
                  </>
                )}
                {isAdmin && info.status && (
                  <Card mt="lg">
                    <LocationStatus
                      locationId={id}
                      fetchData={fetchData}
                      status={info.status}
                    />
                  </Card>
                )}
              </React.Fragment>
            }
          />
          <Route
            path="loot-locker"
            element={
              <div>
                <LootLockerManagement locationId={id} />
              </div>
            }
          />
          {!isProfile && [
            <Route
              key="campaigns"
              path="campaigns"
              element={
                <React.Fragment>
                  <CampaignManagement locationId={id} />
                </React.Fragment>
              }
            />,
            <Route
              key="contesting"
              path="contesting"
              element={
                <React.Fragment>
                  <LocationContesting locationId={id} />
                </React.Fragment>
              }
            />,
            <Route
              key="managers"
              path="managers"
              element={
                <div>
                  <ManagerManagement locationId={id} />
                </div>
              }
            />,
            <Route
              key="keywords"
              path="keywords"
              element={
                <div>
                  <KeywordManagement locationId={id} />
                </div>
              }
            />,
            <Route
              key="settings"
              path="settings"
              element={
                <React.Fragment>
                  <LocationSettings locationId={id} />
                </React.Fragment>
              }
            />,
            <Route
              path="segments"
              element={
                <div>
                  <UserGroupManagement locationId={id} />
                </div>
              }
            />,
            <Route
              key="affiliates"
              path="affiliates"
              element={
                <div>
                  <AffiliateJoinManagement locationId={id} />
                </div>
              }
            />,
          ]}
          {/* <Route
              key="messages"
              path="messages"
              element={
                <div>
                  <MessageList locationId={id} />
                </div>
              }
            /> */}
          <Route
            path="assets"
            element={
              <React.Fragment>
                <AssetManagement locationId={id} title={`Uploads`} />
              </React.Fragment>
            }
          />
          <Route
            path="asset-library"
            element={
              <React.Fragment>
                <AssetManagement locationId={id} library />
              </React.Fragment>
            }
          />
          {info.variety === locationVariety.station && [
            <Route
              path="play"
              element={
                <div>
                  <StationFeedLoaderButton locationId={id} />
                  <StationPlays locationId={id} />
                </div>
              }
            />,
            <Route
              path="song-categories"
              element={
                <div>
                  <SongQueryManagement locationId={id} />
                </div>
              }
            />,
          ]}
          <Route
            path="efforts"
            element={
              <React.Fragment>
                <EffortManagement locationId={id} showCreate={false} />
              </React.Fragment>
            }
          />
          <Route
            path="look-and-feel"
            element={
              <React.Fragment>
                {info.variety !== locationVariety.dealer ? (
                  <Tabs
                    variant="outline"
                    defaultValue="mixer"
                    keepMounted={false}
                  >
                    <Tabs.List mr="xs">
                      <Tabs.Tab value="mixer">Mixer Settings</Tabs.Tab>
                      <Tabs.Tab value="email">Email Settings</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="mixer">
                      <EntitySettingsCode locationId={id} />
                    </Tabs.Panel>
                    <Tabs.Panel value="email">
                      <EmailAppConfig
                        locationId={id}
                        name={
                          info?.settings_code?.settings?.settings
                            ?.email_branding?.entity_name || info.name
                        }
                        replyTo={
                          info?.settings_code?.settings?.settings
                            ?.email_branding?.reply_to
                        }
                        color={
                          info.settings_code &&
                          info.settings_code.settings &&
                          info.settings_code.settings.settings
                            ? info.settings_code.settings.settings.color
                            : "blue"
                        }
                      />
                    </Tabs.Panel>
                  </Tabs>
                ) : (
                  <>
                    <Title order={5} mb="xs" mt="xs">
                      Email Settings
                    </Title>
                    <EmailAppConfig
                      locationId={id}
                      name={
                        info?.settings_code?.settings?.settings?.email_branding
                          ?.entity_name || info.name
                      }
                      replyTo={
                        info?.settings_code?.settings?.settings?.email_branding
                          ?.reply_to
                      }
                      color={
                        info.settings_code &&
                        info.settings_code.settings &&
                        info.settings_code.settings.settings
                          ? info.settings_code.settings.settings.color
                          : "blue"
                      }
                    />
                  </>
                )}
              </React.Fragment>
            }
          />
          <Route
            path="geospots"
            element={
              <div>
                <GeospotManagement locationId={id} />
              </div>
            }
          />
          {/* {postmarkServerId && info.allow_email_marketing && ( */}
          {!managerInfo && (
            <Route
              path="bulk-communication"
              element={
                <div>
                  <BulkCommunicationManagement locationId={id} />
                </div>
              }
            />
          )}
          {showSms && (
            <>
              <Route
                path="sms-messaging"
                key="sms-messaging"
                element={
                  <>
                    <LocationSms
                      locationId={id}
                      info={info}
                      fetchData={fetchData}
                    />
                  </>
                }
              />
              ,
            </>
          )}
        </Routes>
      )}
    </div>
  );
}

function LocationStatus({ locationId, fetchData, status }) {
  function changeStatus() {
    axios
      .get(`/locations/${locationId}/change-status/`)
      .then(() => {
        toast.success("Updated!");
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  return (
    <React.Fragment>
      <Switch
        color="green"
        description={
          status === 1
            ? "Turn off to disable this location but preserve all of their data."
            : "Turn on to reactivate this location."
        }
        label="Activation Status"
        onClick={() => changeStatus()}
        checked={status === 1}
      />
    </React.Fragment>
  );
}

function FeedLoaderPolling({ locationId, fetchData, enabled }) {
  function onChange() {
    axios
      .post(`/locations/${locationId}/toggle-feed-polling/`, {
        location_id: locationId,
      })
      .then(() => {
        toast.success("Updated!");
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  return (
    <React.Fragment>
      <Switch
        color="green"
        label="Feed Loader Polling"
        onChange={onChange}
        checked={enabled}
      />
    </React.Fragment>
  );
}

const FormattedLocationForm = ({ info, fetchData, id }) => (
  <LocationForm
    address1={info.address_1}
    address2={info.address_2}
    allowCampaignCreate={info.allow_campaign_create}
    callLetters={info.call_letters}
    city={info.city}
    country={info.country}
    feedLoaderFilename={info.feed_loader_filename}
    frequency={info.frequency}
    genre={info.genre}
    id={id}
    name={info.name}
    onSuccess={fetchData}
    otpDisplayName={info.otp_display_name}
    state={info.state_province}
    variety={info.variety}
    textOnly={info.text_only}
    thirdPartyIdent={info.third_party_ident}
    timezone={info.timezone}
    zip={info.zip}
  />
);

const LocationSms = ({ fetchData, locationId, info }) => {
  const smsInfo = info.sms_info;

  const items = [
    // {
    //   title: "Transactional SMS Messaging",
    //   context: "transactional_sms",
    //   sms_info_key: "sms_transactional_info",
    //   allowed: info.allow_transactional_sms,
    //   ready: smsInfo?.sms_transactional_ready,
    //   active: smsInfo?.sms_transactional_active,
    // },
    {
      title: "Conversational SMS Messaging",
      context: "conversational_sms",
      sms_info_key: "sms_conversational_info",
      allowed: info.allow_conversational_sms,
      ready: smsInfo?.sms_conversational_ready,
      active: smsInfo?.sms_conversational_active,
    },
    {
      title: "SMS Marketing (where marketing messages are sent from)",
      context: "sms_marketing",
      sms_info_key: "sms_marketing_info",
      // allowed: smsInfo.can_activate_sms_marketing,
      allowed: info.allow_sms_marketing,
      ready: smsInfo?.sms_marketing_ready,
      active: smsInfo?.sms_marketing_active,
    },
    {
      title: "Double Opt-In",
      context: "sms_double_opt_in",
      sms_info_key: "sms_double_opt_in_info",
      allowed: info.allow_sms_marketing,
      ready: smsInfo?.sms_double_opt_in_ready,
      active: smsInfo?.sms_double_opt_in_active,
    },
  ]
    .filter((f) =>
      info.variety === locationVariety.station
        ? true
        : !["conversational_sms"].includes(f.context)
    )
    .map((m) => ({
      ...m,
      messagingService: smsInfo?.[m.sms_info_key]?.messaging_service_ident
        ? smsInfo?.[m.sms_info_key]
        : null,
      sms: smsInfo?.[m.sms_info_key]?.phone_id
        ? smsInfo?.[m.sms_info_key]
        : null,
      reqData: {
        location_id: locationId,
      },
    }));

  return (
    <div>
      {items.map((m, i) => (
        <Box key={i} mt="lg">
          <EntityMessagingSms fetchData={fetchData} {...m} />
        </Box>
      ))}
    </div>
  );
};
